import React, { useState } from "react";
import { toast } from "sonner";
import { driverApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, X as XIcon } from "lucide-react";

const AVAILABILITY_STATUSES = ["available", "unavailable", "on_tour", "off_duty", "hold"];
const INSURANCE_STATUSES = ["active", "expired", "pending", "not_required", "unknown"];
// F3 · Owner-mandated Driver employment lifecycle. Distinct from the
// legacy free-string `driver.role` (untouched).
const EMPLOYMENT_STATUSES = ["active", "on_hold", "quit", "terminated"];
// RC5 Item 2 — driver classification enum (mirrors RC4 backend enum
// `full_time_driver` | `co_driver`).
const CLASSIFICATION_OPTIONS = [
  { value: "", label: "— Unspecified —" },
  { value: "full_time_driver", label: "Full-Time Driver" },
  { value: "co_driver", label: "Co-Driver" },
];

export const DriverFormDialog = ({ open, initial, onClose, onSaved, organizations = [] }) => {
  // RC10j — authorized users (same tuple as passport PII) see + edit
  // DOB. For unauthorized viewers, the field is omitted from the
  // dialog entirely (self-consistent with the API: unauthorized users
  // receive `date_of_birth: null` and lack the WRITE_ROLES grant).
  const { user } = useAuth();
  const DOB_PRIVILEGED = !!user && (user.role === "super_admin" || user.role === "admin");
  const [form, setForm] = useState({
    first_name: initial?.first_name || "",
    last_name: initial?.last_name || "",
    display_name: initial?.display_name || "",
    email: initial?.email || "",
    phone_primary: initial?.phone_primary || "",
    employment_type: initial?.employment_type || "employee",
    cdl_number: initial?.cdl_number || "",
    cdl_state: initial?.cdl_state || "",
    cdl_class: initial?.cdl_class || "A",
    cdl_expiration: initial?.cdl_expiration || "",
    medical_card_expiration: initial?.medical_card_expiration || "",
    hire_date: initial?.hire_date || "",
    // RC10j — nullable DOB. Form holds "" when blank; serialize to null
    // on submit (RC10g Fix 1 pattern).
    date_of_birth: initial?.date_of_birth || "",
    active: initial?.active ?? true,
    // F3 · Employment status enum (distinct from legacy `role`).
    employment_status: initial?.employment_status || "active",
    // F3 · Passport (fields already exist on backend model).
    passport_number: initial?.passport_number || "",
    passport_expiration: initial?.passport_expiration || "",
    notes: initial?.notes || "",
    // Front Office Data Reconciliation Wave
    availability_status: initial?.availability_status || "",
    available_from: initial?.available_from || "",
    available_until: initial?.available_until || "",
    employer_organization_id: initial?.employer_organization_id || "",
    insurance_status: initial?.insurance_status || "",
    insurance_expiration: initial?.insurance_expiration || "",
    trailer_qualified: initial?.trailer_qualified ?? null,
    trailer_restriction_reason: initial?.trailer_restriction_reason || "",
    // RC5 · Item 2 — classification + annual premium tracking. Rates
    // are NEVER captured here.
    driver_classification: initial?.driver_classification || "",
    annual_insurance_premium_paid: initial?.annual_insurance_premium_paid ?? false,
    insurance_coverage_year:
      initial?.insurance_coverage_year !== null &&
      initial?.insurance_coverage_year !== undefined
        ? String(initial.insurance_coverage_year)
        : "",
    insurance_payment_date: initial?.insurance_payment_date || "",
    insurance_notes: initial?.insurance_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [availOpen, setAvailOpen] = useState(!!initial?.id);
  React.useEffect(() => {
    // Keep the section auto-open when the parent hydrates `initial` after mount.
    if (initial?.id) setAvailOpen(true);
  }, [initial?.id]);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // RC10j — inline DOB validation. Future date -> inline error +
  // disabled Save. Minimum-date sanity ("1900-01-01") is advisory;
  // legacy earlier values display but are still accepted on re-save.
  const dobError = React.useMemo(() => {
    if (!DOB_PRIVILEGED) return "";
    const raw = (form.date_of_birth || "").trim();
    if (raw === "") return "";
    // HTML date inputs always serialize as YYYY-MM-DD, but a legacy
    // record could carry a malformed value.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "Enter a valid date.";
    const todayIso = new Date().toISOString().slice(0, 10);
    if (raw > todayIso) return "Date of birth cannot be in the future.";
    return "";
  }, [form.date_of_birth, DOB_PRIVILEGED]);

  const submit = async (e) => {
    e.preventDefault();
    if (dobError) return;
    setSaving(true);
    try {
      // RC5 Item 3 — `default_day_rate`, `equipment_qualifications`, and
      // `equipment_restrictions` are intentionally OMITTED from this
      // payload so a save from this dialog does NOT destructively wipe
      // stored values. Backend model + API still accept and return them
      // for backward compatibility with imports/integrations.
      const payload = {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        display_name: form.display_name || `${form.first_name} ${form.last_name}`.trim(),
        email: form.email || null,
        phone_primary: form.phone_primary || null,
        employment_type: form.employment_type || null,
        cdl_number: form.cdl_number || null,
        cdl_state: form.cdl_state || null,
        cdl_class: form.cdl_class || null,
        cdl_expiration: form.cdl_expiration || null,
        medical_card_expiration: form.medical_card_expiration || null,
        hire_date: form.hire_date || null,
        active: form.active,
        employment_status: form.employment_status || "active",
        passport_number: form.passport_number || null,
        passport_expiration: form.passport_expiration || null,
        notes: form.notes || null,
        // RC10j — DOB serialized to null when blank/whitespace
        // (RC10g Fix 1 pattern). Only authorized roles include the
        // key at all; unauthorized omit it so the backend 403 guard
        // can never fire for a false-positive dialog click.
        ...(DOB_PRIVILEGED
          ? {
              date_of_birth:
                form.date_of_birth && form.date_of_birth.trim() !== ""
                  ? form.date_of_birth
                  : null,
            }
          : {}),
        // Wave fields
        availability_status: form.availability_status || null,
        available_from: form.available_from || null,
        available_until: form.available_until || null,
        employer_organization_id: form.employer_organization_id || null,
        insurance_status: form.insurance_status || null,
        insurance_expiration: form.insurance_expiration || null,
        trailer_qualified:
          form.trailer_qualified === null || form.trailer_qualified === ""
            ? null
            : Boolean(form.trailer_qualified),
        trailer_restriction_reason: form.trailer_restriction_reason || null,
        // RC5 · Item 2 · classification + insurance-payment metadata.
        driver_classification: form.driver_classification || null,
        annual_insurance_premium_paid: Boolean(form.annual_insurance_premium_paid),
        insurance_coverage_year:
          form.insurance_coverage_year === "" || form.insurance_coverage_year === null
            ? null
            : Number(form.insurance_coverage_year),
        insurance_payment_date: form.insurance_payment_date || null,
        insurance_notes: form.insurance_notes || null,
      };
      if (initial?.id) await driverApi.patch(initial.id, payload);
      else await driverApi.create(payload);
      toast.success(initial ? "Driver updated" : "Driver created");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col" data-testid="driver-form-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-display">
            {initial ? "Edit Driver" : "New Driver"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-[13px] px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-2 gap-2">
            <Input label="First Name" required value={form.first_name} onChange={(v) => setField("first_name", v)} testid="df-first" />
            <Input label="Last Name" required value={form.last_name} onChange={(v) => setField("last_name", v)} testid="df-last" />
          </div>
          <Input label="Display Name" value={form.display_name} onChange={(v) => setField("display_name", v)} testid="df-display" />
          <div className="grid grid-cols-2 gap-2">
            <Input label="Email" type="email" value={form.email} onChange={(v) => setField("email", v)} testid="df-email" />
            <Input label="Phone" value={form.phone_primary} onChange={(v) => setField("phone_primary", v)} testid="df-phone" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select label="Employment" value={form.employment_type} onChange={(v) => setField("employment_type", v)} options={["employee", "contractor", "other"]} testid="df-employment" />
            {/* RC5 Item 2 — Driver Classification. */}
            <label className="block">
              <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">Driver Classification</div>
              <select
                data-testid="df-driver-classification"
                value={form.driver_classification}
                onChange={(e) => setField("driver_classification", e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
              >
                {CLASSIFICATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input label="CDL #" value={form.cdl_number} onChange={(v) => setField("cdl_number", v)} testid="df-cdl-number" />
            <Input label="CDL State" value={form.cdl_state} onChange={(v) => setField("cdl_state", v)} testid="df-cdl-state" />
            <Select label="CDL Class" value={form.cdl_class} onChange={(v) => setField("cdl_class", v)} options={["A", "B", "C", "other"]} testid="df-cdl-class" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input label="CDL Expiration" type="date" value={form.cdl_expiration} onChange={(v) => setField("cdl_expiration", v)} testid="df-cdl-exp" />
            <Input label="Medical Card Expiration" type="date" value={form.medical_card_expiration} onChange={(v) => setField("medical_card_expiration", v)} testid="df-med-exp" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {/* RC10b Item 1 — Hire Date UI binding. Field already exists on the backend
                Driver model; nullable so legacy records remain unchanged until a value is set. */}
            <Input label="Hire Date" type="date" value={form.hire_date} onChange={(v) => setField("hire_date", v)} testid="df-hire-date" />
            {/* RC10j — Date of Birth. Only authorized roles (super_admin,
                admin) see the field; unauthorized roles never render it.
                Server-side 403 enforced on PATCH with the key present. */}
            {DOB_PRIVILEGED && (
              <label className="block" data-testid="df-dob-wrapper">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel">
                    Date of Birth
                  </div>
                  {form.date_of_birth && (
                    <button
                      type="button"
                      data-testid="df-dob-clear"
                      onClick={() => setField("date_of_birth", "")}
                      className="text-[10.5px] text-steel hover:text-critical-red inline-flex items-center gap-1"
                      aria-label="Clear date of birth"
                    >
                      <XIcon size={11} aria-hidden="true" /> Clear
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  data-testid="df-dob"
                  aria-invalid={dobError ? "true" : "false"}
                  aria-describedby={dobError ? "df-dob-error" : undefined}
                  max={new Date().toISOString().slice(0, 10)}
                  value={form.date_of_birth}
                  onChange={(e) => setField("date_of_birth", e.target.value)}
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                />
                {dobError && (
                  <div
                    id="df-dob-error"
                    data-testid="df-dob-error"
                    role="alert"
                    className="mt-1 text-[11.5px] text-critical-red"
                  >
                    {dobError}
                  </div>
                )}
              </label>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input label="Passport #" value={form.passport_number} onChange={(v) => setField("passport_number", v)} testid="df-passport-number" />
            <Input label="Passport Expiration" type="date" value={form.passport_expiration} onChange={(v) => setField("passport_expiration", v)} testid="df-passport-exp" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Employment Status"
              value={form.employment_status}
              onChange={(v) => setField("employment_status", v)}
              options={EMPLOYMENT_STATUSES}
              testid="df-employment-status"
            />
            <div className="text-[11px] text-steel self-end pb-1.5">
              Passport scan is uploaded from the Driver → Documents tab.
            </div>
          </div>

          <button
            type="button"
            data-testid="df-avail-toggle"
            onClick={() => setAvailOpen((v) => !v)}
            className="mt-2 flex items-center gap-1 text-[12px] text-coach-blue hover:underline"
          >
            {availOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Availability, Insurance & Equipment {availOpen ? "" : "(new)"}
          </button>

          {availOpen && (
            <div className="border-l-2 border-coach-silver pl-3 space-y-2 pt-1" data-testid="df-avail-section">
              <div className="grid grid-cols-3 gap-2">
                <Select
                  label="Availability Status"
                  value={form.availability_status}
                  onChange={(v) => setField("availability_status", v)}
                  options={["", ...AVAILABILITY_STATUSES]}
                  testid="df-availability-status"
                />
                <Input label="Available From" type="date" value={form.available_from} onChange={(v) => setField("available_from", v)} testid="df-available-from" />
                <Input label="Available Until" type="date" value={form.available_until} onChange={(v) => setField("available_until", v)} testid="df-available-until" />
              </div>
              <label className="block">
                <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">Employer Organization</div>
                <select
                  data-testid="df-employer-org"
                  value={form.employer_organization_id}
                  onChange={(e) => setField("employer_organization_id", e.target.value)}
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
                >
                  <option value="">— None (in-house) —</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>{o.display_name}</option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  label="Insurance Status"
                  value={form.insurance_status}
                  onChange={(v) => setField("insurance_status", v)}
                  options={["", ...INSURANCE_STATUSES]}
                  testid="df-insurance-status"
                />
                <Input label="Insurance Expiration" type="date" value={form.insurance_expiration} onChange={(v) => setField("insurance_expiration", v)} testid="df-insurance-exp" />
              </div>
              {/* RC5 Item 2 — insurance payment tracking. Rates are NEVER captured. */}
              <div className="grid grid-cols-3 gap-2">
                <label className="block">
                  <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">Annual Premium Paid</div>
                  <select
                    data-testid="df-annual-insurance-premium-paid"
                    value={form.annual_insurance_premium_paid ? "true" : "false"}
                    onChange={(e) => setField("annual_insurance_premium_paid", e.target.value === "true")}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </label>
                <Input
                  label="Coverage Year"
                  type="number"
                  value={form.insurance_coverage_year}
                  onChange={(v) => setField("insurance_coverage_year", v)}
                  testid="df-insurance-coverage-year"
                />
                <Input
                  label="Payment Date"
                  type="date"
                  value={form.insurance_payment_date}
                  onChange={(v) => setField("insurance_payment_date", v)}
                  testid="df-insurance-payment-date"
                />
              </div>
              <label className="block">
                <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">Insurance Notes</div>
                <textarea
                  data-testid="df-insurance-notes"
                  value={form.insurance_notes}
                  onChange={(e) => setField("insurance_notes", e.target.value)}
                  rows={2}
                  maxLength={2000}
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                />
              </label>
              <div className="grid grid-cols-2 gap-2 items-end">
                <label className="block">
                  <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">Trailer Qualified</div>
                  <select
                    data-testid="df-trailer-qualified"
                    value={form.trailer_qualified === null || form.trailer_qualified === "" ? "" : form.trailer_qualified ? "true" : "false"}
                    onChange={(e) => setField("trailer_qualified", e.target.value === "" ? null : e.target.value === "true")}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
                  >
                    <option value="">— Unspecified —</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <Input
                  label="Trailer Restriction Reason"
                  value={form.trailer_restriction_reason}
                  onChange={(v) => setField("trailer_restriction_reason", v)}
                  testid="df-trailer-restriction-reason"
                />
              </div>
              <div className="text-[10.5px] text-steel">
                Customer-specific restrictions are managed on the driver detail page.
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-[12px] pt-2">
            <input type="checkbox" data-testid="df-active" checked={form.active} onChange={(e) => setField("active", e.target.checked)} />
            Active
          </label>
          <DialogFooter className="pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12.5px] text-steel border border-coach-silver rounded-sm">Cancel</button>
            <button data-testid="df-submit" disabled={saving || !!dobError} className="px-4 py-1.5 text-[12.5px] font-medium bg-coach-blue text-white rounded-sm disabled:opacity-60">
              {saving ? "Saving…" : initial ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Input = ({ label, value, onChange, type = "text", required, testid }) => (
  <label className="block">
    <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">
      {label} {required && <span className="text-critical-red">*</span>}
    </div>
    <input
      type={type}
      required={required}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
    />
  </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">{label}</div>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o === "" ? "— None —" : o.replace(/_/g, " ")}
        </option>
      ))}
    </select>
  </label>
);
